/**
 * Retro Board widget preset.
 *
 * Architecture notes:
 *   - Only `data.cards` (and `presetId`) are stored in Loro. There is NO `html`
 *     field — the board is rendered as a first-class React component
 *     (RetroBoardWidget) that reads cards directly from Loro data.
 *   - `html` on the preset is set to an empty string. WidgetNode detects
 *     `presetId === 'retro-board'` and bypasses the dangerouslySetInnerHTML
 *     path entirely.
 *   - The canvas agent sees a structured text representation of the board
 *     via a special case in backend/services/neuraflow/domain/chain/canvas.go.
 */
import type { WidgetPreset } from "../types";
import previewHtml from "./preview.html?raw";
import { RetroBoardSummarizeButton } from "./RetroBoardSummarizeButton";
import { RetroBoardWidget } from "./RetroBoardWidget";

// ─── Public types ────────────────────────────────────────────────────────────

export interface RetroCard {
  id: string;
  columnIndex: 0 | 1 | 2 | 3;
  content: string;
  authorName: string;
  authorAvatar?: string;
  votes: string[]; // voter user IDs — toggled by retro:vote-card
  createdAt: number; // Date.now() timestamp
}

// ─── Constants (also consumed by RetroBoardWidget) ────────────────────────────

export const RETRO_COLUMN_NAMES = [
  "Kudos",
  "Keep Doing",
  "Stop Doing",
  "Start Doing",
] as const;

export const RETRO_COLUMN_COLORS = [
  // Kudos — amber/gold
  {
    pip: "#d97706",
    bg: "rgba(254,243,199,.45)",
    border: "rgba(180,130,0,.50)",
    head: "rgba(217,119,6,.55)",
    name: "#78350f",
    bgDark: "rgba(251,191,36,.15)",
    borderDark: "rgba(251,191,36,.65)",
    headDark: "rgba(251,191,36,.70)",
    nameDark: "#fcd34d",
  },
  // Keep Doing — green
  {
    pip: "#16a34a",
    bg: "rgba(187,247,208,.45)",
    border: "rgba(22,163,74,.45)",
    head: "rgba(22,163,74,.55)",
    name: "#14532d",
    bgDark: "rgba(34,197,94,.15)",
    borderDark: "rgba(74,222,128,.55)",
    headDark: "rgba(74,222,128,.65)",
    nameDark: "#4ade80",
  },
  // Stop Doing — red
  {
    pip: "#dc2626",
    bg: "rgba(254,202,202,.45)",
    border: "rgba(220,38,38,.45)",
    head: "rgba(220,38,38,.55)",
    name: "#7f1d1d",
    bgDark: "rgba(239,68,68,.15)",
    borderDark: "rgba(248,113,113,.55)",
    headDark: "rgba(248,113,113,.65)",
    nameDark: "#fca5a5",
  },
  // Start Doing — indigo
  {
    pip: "#4f46e5",
    bg: "rgba(199,210,254,.45)",
    border: "rgba(99,102,241,.45)",
    head: "rgba(79,70,229,.55)",
    name: "#1e1b4b",
    bgDark: "rgba(99,102,241,.15)",
    borderDark: "rgba(129,140,248,.55)",
    headDark: "rgba(129,140,248,.65)",
    nameDark: "#a5b4fc",
  },
] as const;

/** Sticky note colors per column — used when creating companion text notes. */
export const RETRO_NOTE_COLORS = [
  "#fef08a",
  "#d1fae5",
  "#fee2e2",
  "#dbeafe",
] as const;

/** Minimum zone height with no cards. */
export const RETRO_MIN_ZONE_H = 560;

/** Fixed vertical overhead from board top to zone start (header + col-header + padding). */
export const RETRO_BOARD_OVERHEAD = 125;

/** Height per card slot (estimated card body + gap). */
export const RETRO_CARD_SLOT_H = 120; // card ~110px + 10px gap

/** Height of the "+ New card" button at the bottom of each column. */
export const RETRO_ADD_BTN_H = 44;

/**
 * Calculate the zone height needed to fit the current set of cards.
 * Takes the tallest column as the reference so all columns are the same height.
 */
export function calcZoneHeight(cards: RetroCard[]): number {
  const colH = ([0, 1, 2, 3] as const).map((ci) => {
    const count = cards.filter((c) => c.columnIndex === ci).length;
    return count * RETRO_CARD_SLOT_H + RETRO_ADD_BTN_H + 16;
  });
  return Math.max(RETRO_MIN_ZONE_H, Math.max(...colH));
}

// ─── Preset ───────────────────────────────────────────────────────────────────

export const RETRO_BOARD: WidgetPreset = {
  id: "retro-board",
  label: "Retro Board 🔄",
  defaultWidth: 1600,
  defaultHeight: 685, // RETRO_BOARD_OVERHEAD(125) + RETRO_MIN_ZONE_H(560)
  zIndex: -1,
  component: RetroBoardWidget,
  toolbarSlot: RetroBoardSummarizeButton,
  resizable: {
    minWidth: 800,
    maxWidth: 3200,
    minHeight: RETRO_BOARD_OVERHEAD + RETRO_MIN_ZONE_H,
    scaleContent: false, // RetroBoardWidget uses w-full/h-full — no CSS scale
  },
  // previewHtml — static thumbnail shown in the widget picker popover.
  // Uses a neutral dark/light scheme that reads in both themes.
  previewHtml: previewHtml,
  initialData: {
    cards: [],
  },
};
